
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Empresa, Produto, Bairro, Categoria, AdicionalCategoria, AdicionalItem, ClientePerfil, ClienteEndereco } from '../types';
import { supabaseService } from '../supabaseService';
import { MenuHeader } from '../components/MenuHeader';
import { Cart } from '../components/Cart';
import { CustomerAuthModal } from '../components/CustomerAuthModal';
import { formatCurrency } from '../constants';
import { useCarrinho } from '../hooks/useCarrinho';

interface MenuViewProps {
  slug: string;
  // Fix: onBack should be a callback function () => void, not void.
  onBack?: () => void;
}

const FORMAS_PAGAMENTO = [
  { id: 'pix', label: 'PIX' },
  { id: 'cartao', label: 'Cartão Débito/Crédito' },
  { id: 'dinheiro', label: 'Dinheiro' }
];

export const MenuView: React.FC<MenuViewProps> = ({ slug }) => {
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [bairros, setBairros] = useState<Bairro[]>([]);
  const [adicionaisCats, setAdicionaisCats] = useState<AdicionalCategoria[]>([]);
  
  const [customer, setCustomer] = useState<ClientePerfil | null>(null);
  const [customerAddresses, setCustomerAddresses] = useState<ClienteEndereco[]>([]);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const { cart, addToCart, updateQuantity, clearCart, total } = useCarrinho();
  
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewingProduct, setViewingProduct] = useState<Produto | null>(null);

  const [tempObservation, setTempObservation] = useState('');
  const [tempAdicionais, setTempAdicionais] = useState<AdicionalItem[]>([]);
  const [tempQty, setTempQty] = useState(1);

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const categoryRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  const [cliente, setCliente] = useState({ 
    nome: '', 
    whatsapp: '', 
    endereco: '', 
    obs: '',
    formaPagamento: '',
    bairroId: ''
  });

  // SOFT DELETE: Filtragem de conteúdo ativo para o cliente
  const categoriasAtivas = useMemo(() => categorias.filter(c => c.ativo), [categorias]);
  const produtosAtivos = useMemo(() => produtos.filter(p => p.ativo), [produtos]);
  const adicionaisCatsAtivas = useMemo(() => {
    return adicionaisCats
      .filter(ac => ac.ativo)
      .map(ac => ({
        ...ac,
        itens: ac.itens?.filter(i => i.ativo) || []
      }));
  }, [adicionaisCats]);

  const loadCustomerData = async () => {
    const profile = await supabaseService.getProfile();
    if (profile) {
      setCustomer(profile);
      setCliente(prev => ({ ...prev, nome: profile.nome, whatsapp: profile.whatsapp }));
      const addresses = await supabaseService.getAddresses();
      setCustomerAddresses(addresses);
    } else {
      setCustomer(null);
      setCustomerAddresses([]);
    }
  };

  useEffect(() => {
    async function load() {
      const e = await supabaseService.getEmpresaBySlug(slug);
      if (e) {
        setEmpresa(e);
        const [p, b, c, ac] = await Promise.all([
          supabaseService.getProdutos(e.id),
          supabaseService.getBairros(e.id),
          supabaseService.getCategorias(e.id),
          supabaseService.getAdicionaisCategorias(e.id)
        ]);
        setProdutos(p);
        setBairros(b);
        setCategorias(c);
        setAdicionaisCats(ac);
        
        const firstActive = c.find(cat => cat.ativo);
        if (firstActive) setActiveCategory(firstActive.id);
        
        await loadCustomerData();
      }
      setLoading(false);
    }
    load();
  }, [slug]);

  const handleLogout = async () => {
    await supabaseService.signOut();
    setCustomer(null);
    setCustomerAddresses([]);
    setCliente({ nome: '', whatsapp: '', endereco: '', obs: '', formaPagamento: '', bairroId: '' });
  };

  const handleSelectAddress = (addr: ClienteEndereco) => {
    setCliente(prev => ({
      ...prev,
      endereco: addr.endereco,
      bairroId: addr.bairro_id
    }));
  };

  const scrollToCategory = (id: string) => {
    setActiveCategory(id);
    const element = categoryRefs.current[id];
    if (element) {
      const headerOffset = 180;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleAddToCart = () => {
    if (!viewingProduct) return;
    addToCart(viewingProduct, tempQty, tempObservation, tempAdicionais);
    setViewingProduct(null);
    setTempObservation('');
    setTempAdicionais([]);
    setTempQty(1);
  };

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empresa) return;
    setIsSubmitting(true);
    const bairro = bairros.find(b => b.id === cliente.bairroId);
    const taxa = bairro?.taxa || 0;
    const clienteData = {
      nome: cliente.nome,
      whatsapp: cliente.whatsapp,
      endereco: cliente.endereco,
      total: total + taxa,
      taxa_entrega: taxa,
      bairro_nome: bairro?.nome,
      forma_pagamento: cliente.formaPagamento,
      observacoes: cliente.obs
    };
    const itensPedido = cart.map(item => ({
      produto_id: item.id,
      quantidade: item.quantidade,
      preco_unitario: item.preco + (item.adicionais_selecionados?.reduce((a, ad) => a + ad.preco, 0) || 0),
      produto_nome: item.nome,
      observacao: item.observacao,
      // Se houver adicionais, a edge function deve saber lidar
      adicionais: item.adicionais_selecionados
    }));
    try {
      await supabaseService.criarPedido(empresa.id, clienteData, itensPedido);
      
      let msg = `*NOVO PEDIDO - ${empresa.nome}*\n\n*Cliente:* ${cliente.nome}\n*WhatsApp:* ${cliente.whatsapp}\n*Endereço:* ${cliente.endereco} (${bairro?.nome || 'Não informado'})\n*Pagamento:* ${cliente.formaPagamento.toUpperCase()}\n`;
      if (cliente.obs) msg += `*Obs:* ${cliente.obs}\n`;
      msg += `\n*ITENS:*\n`;
      cart.forEach(it => {
        msg += `${it.quantidade}x ${it.nome} - ${formatCurrency(it.preco)}\n`;
        it.adicionais_selecionados?.forEach(ad => { msg += `  + ${ad.nome} (${formatCurrency(ad.preco)})\n`; });
        if (it.observacao) msg += `  _Obs: ${it.observacao}_\n`;
      });
      msg += `\n*Taxa de Entrega:* ${formatCurrency(taxa)}\n*TOTAL:* ${formatCurrency(total + taxa)}`;
      window.open(`https://wa.me/${empresa.telefone_whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`, '_blank');
      clearCart();
      setIsCheckoutOpen(false);
      alert("Pedido enviado com sucesso!");
    } catch (err: any) { alert("Erro ao enviar pedido: " + err.message); }
    finally { setIsSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!empresa) return <div className="min-h-screen flex items-center justify-center bg-white"><h2 className="text-xl font-black text-slate-400">Restaurante não encontrado</h2></div>;

  return (
    <div className="min-h-screen bg-slate-50 pb-32 font-sans">
      <MenuHeader 
        empresa={empresa} 
        customer={customer} 
        onOpenLogin={() => setIsAuthModalOpen(true)} 
        onLogout={handleLogout} 
      />

      {categoriasAtivas.length > 0 && (
        <div className="sticky top-[116px] z-40 bg-slate-50/90 backdrop-blur-md border-b border-slate-200/50">
          <div className="max-w-xl mx-auto px-6 overflow-x-auto no-scrollbar flex items-center gap-6 py-4">
            {categoriasAtivas.map(cat => (
              <button
                key={cat.id}
                onClick={() => scrollToCategory(cat.id)}
                className={`whitespace-nowrap text-[10px] font-black uppercase tracking-widest transition-all duration-300 relative py-1 ${
                  activeCategory === cat.id ? 'text-orange-600 scale-110' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {cat.nome}
                {activeCategory === cat.id && (
                  <span className="absolute -bottom-1 left-0 right-0 h-1 bg-orange-600 rounded-full animate-fade-in"></span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <main className="max-w-xl mx-auto px-6 py-8 space-y-12">
        {categoriasAtivas.map(cat => (
          <section 
            key={cat.id} 
            ref={el => { categoryRefs.current[cat.id] = el; }}
            className="space-y-5 scroll-mt-40"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                <span className={`w-1.5 h-6 rounded-full transition-colors duration-500 ${activeCategory === cat.id ? 'bg-orange-600 shadow-lg shadow-orange-200' : 'bg-slate-300'}`}></span>
                {cat.nome}
              </h2>
              <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">
                {produtosAtivos.filter(p => p.categoria === cat.nome).length} Itens
              </span>
            </div>
            
            <div className="flex flex-col gap-4">
              {produtosAtivos.filter(p => p.categoria === cat.nome).map(prod => (
                <div 
                  key={prod.id} 
                  onClick={() => { setViewingProduct(prod); setTempQty(1); }} 
                  className="bg-white p-4 rounded-[2.5rem] border border-slate-100 flex items-center gap-5 cursor-pointer hover:shadow-2xl hover:shadow-slate-200/60 transition-all active:scale-[0.98] group relative overflow-hidden"
                >
                  <div className="w-24 h-24 bg-slate-50 rounded-2xl overflow-hidden shrink-0 border border-slate-100/50 shadow-inner group-hover:scale-105 transition-transform duration-500">
                    {prod.imagem_url ? (
                      <img src={prod.imagem_url} alt={prod.nome} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-slate-50">
                        <span className="text-[10px] font-black text-slate-200 uppercase tracking-widest">Delyon</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 pr-8">
                    <h3 className="font-extrabold text-slate-900 leading-tight mb-1 text-[15px] group-hover:text-orange-600 transition-colors">
                      {prod.nome}
                    </h3>
                    <p className="text-[11px] text-slate-400 font-medium line-clamp-2 mb-3 leading-snug">
                      {prod.descricao}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-black text-slate-900">{formatCurrency(prod.preco)}</span>
                    </div>
                  </div>

                  <div className="absolute right-6 top-1/2 -translate-y-1/2">
                    <div className="w-11 h-11 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-300 group-hover:bg-orange-600 group-hover:text-white transition-all duration-300 border border-slate-100">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4"></path>
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>

      {cart.length > 0 && (
        <div className="fixed bottom-6 left-6 right-6 z-[60] max-w-xl mx-auto">
          <button onClick={() => setIsCartOpen(true)} className="w-full bg-slate-900 text-white p-5 rounded-[2rem] flex items-center justify-between shadow-2xl hover:scale-[1.02] transition-all active:scale-[0.98]">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-orange-600 rounded-xl flex items-center justify-center font-black text-sm">
                {cart.length}
              </div>
              <span className="font-black uppercase tracking-widest text-xs">Finalizar Pedido</span>
            </div>
            <span className="text-lg font-black">{formatCurrency(total)}</span>
          </button>
        </div>
      )}

      {viewingProduct && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6 animate-fade-in font-['Inter',_sans-serif]">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setViewingProduct(null)}></div>
          <div className="relative bg-white w-full max-w-xl rounded-t-[2.5rem] sm:rounded-[2.5rem] overflow-hidden flex flex-col max-h-[92vh] shadow-2xl transform transition-transform">
            <div className="h-[35vh] sm:h-64 relative shrink-0 overflow-hidden">
              {viewingProduct.imagem_url ? (
                <img src={viewingProduct.imagem_url} alt={viewingProduct.nome} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center text-slate-300">
                  <span className="font-black uppercase tracking-widest text-[10px]">Delyon</span>
                </div>
              )}
              <button onClick={() => setViewingProduct(null)} className="absolute top-4 right-4 w-9 h-9 bg-slate-900/40 backdrop-blur-md text-white rounded-full flex items-center justify-center border border-white/20">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto custom-scrollbar space-y-6 pb-28">
              <div className="space-y-2">
                <div className="flex justify-between items-start gap-4">
                  <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight leading-tight">{viewingProduct.nome}</h2>
                  <div className="text-xl font-black text-orange-600 shrink-0">{formatCurrency(viewingProduct.preco)}</div>
                </div>
                <p className="text-slate-500 font-medium text-sm leading-relaxed">{viewingProduct.descricao}</p>
              </div>

              {adicionaisCatsAtivas.filter(cat => viewingProduct.adicionais_ids?.includes(cat.id)).map(cat => (
                <div key={cat.id} className="space-y-3">
                  <div className="flex justify-between items-end border-b border-slate-100 pb-1.5">
                    <div>
                      <h4 className="font-bold text-slate-900 uppercase text-[11px] tracking-widest">{cat.nome}</h4>
                      <p className="text-[10px] text-slate-400 font-semibold">Escolha até {cat.maximo}</p>
                    </div>
                    {cat.obrigatorio && <span className="text-[9px] bg-slate-900 text-white px-2 py-0.5 rounded font-bold uppercase">Obrigatório</span>}
                  </div>
                  <div className="space-y-1.5">
                    {cat.itens?.map(item => { 
                      const isSelected = tempAdicionais.some(it => it.id === item.id); 
                      return (
                        <label key={item.id} className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all cursor-pointer ${isSelected ? 'border-orange-600 bg-orange-50/20' : 'border-slate-50 bg-slate-50/30'}`}>
                          <div className="flex items-center gap-3">
                            <input type="checkbox" className="w-4 h-4 accent-orange-600" checked={isSelected} onChange={e => { 
                              if (e.target.checked) { 
                                if (tempAdicionais.filter(it => it.categoria_adicional_id === cat.id).length < cat.maximo) setTempAdicionais([...tempAdicionais, item]); 
                              } else setTempAdicionais(tempAdicionais.filter(it => it.id !== item.id)); 
                            }} />
                            <span className="font-semibold text-slate-700 text-sm">{item.nome}</span>
                          </div>
                          {item.preco > 0 && <span className="text-[11px] font-bold text-orange-600">+{formatCurrency(item.preco)}</span>}
                        </label>
                      ); 
                    })}
                  </div>
                </div>
              ))}

              <div className="space-y-3">
                <h4 className="font-bold text-slate-900 uppercase text-[11px] tracking-widest border-b border-slate-100 pb-1.5">Alguma observação?</h4>
                <textarea className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:border-orange-600 outline-none transition-all resize-none placeholder:text-slate-300" rows={2} placeholder="Ex: tirar cebola..." value={tempObservation} onChange={e => setTempObservation(e.target.value)} />
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-6 bg-white border-t border-slate-100 flex items-center gap-4">
              <div className="flex items-center gap-3 bg-slate-50 p-1.5 rounded-xl border border-slate-100 shrink-0">
                <button onClick={() => setTempQty(Math.max(1, tempQty - 1))} className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-orange-600 font-bold text-lg">-</button>
                <span className="font-black text-slate-900 text-base min-w-[20px] text-center">{tempQty}</span>
                <button onClick={() => setTempQty(tempQty + 1)} className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-orange-600 font-bold text-lg">+</button>
              </div>
              <button onClick={handleAddToCart} className="flex-1 bg-orange-600 text-white h-12 rounded-xl font-bold flex items-center justify-between px-5 shadow-lg shadow-orange-200 hover:bg-orange-700 transition-all active:scale-[0.98]">
                <span className="text-sm">Adicionar</span>
                <span className="text-sm">{formatCurrency((viewingProduct.preco + tempAdicionais.reduce((a, b) => a + b.preco, 0)) * tempQty)}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {isCheckoutOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 animate-fade-in font-['Inter',_sans-serif]">
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-md" onClick={() => setIsCheckoutOpen(false)}></div>
          <form onSubmit={handleCheckout} className="relative bg-white w-full max-w-xl rounded-[3rem] p-10 shadow-2xl space-y-8 max-h-[90vh] overflow-y-auto custom-scrollbar">
            <h2 className="text-3xl font-black tracking-tight text-slate-900">Finalizar Pedido</h2>
            
            {customer && customerAddresses.length > 0 && (
              <div className="space-y-4">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Usar endereço salvo</label>
                <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                  {customerAddresses.map(addr => (
                    <button 
                      key={addr.id} 
                      type="button" 
                      onClick={() => handleSelectAddress(addr)}
                      className={`shrink-0 p-4 rounded-2xl border-2 transition-all text-left min-w-[200px] ${cliente.endereco === addr.endereco ? 'border-orange-600 bg-orange-50/20' : 'border-slate-50 bg-slate-50'}`}
                    >
                      <p className="text-[10px] font-black uppercase text-orange-600 tracking-widest mb-1">{addr.titulo}</p>
                      <p className="text-xs font-bold text-slate-700 line-clamp-1">{addr.endereco}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Seu Nome</label>
                <input required className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:border-orange-600 outline-none transition-all" value={cliente.nome} onChange={e => setCliente({...cliente, nome: e.target.value})} />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">WhatsApp</label>
                <input required className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:border-orange-600 outline-none transition-all" value={cliente.whatsapp} onChange={e => setCliente({...cliente, whatsapp: e.target.value})} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Bairro</label>
                  <select required className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:border-orange-600 outline-none transition-all" value={cliente.bairroId} onChange={e => setCliente({...cliente, bairroId: e.target.value})}>
                    <option value="">Selecione...</option>
                    {bairros.map(b => <option key={b.id} value={b.id}>{b.nome} ({formatCurrency(b.taxa)})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Forma de Pagamento</label>
                  <select required className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:border-orange-600 outline-none transition-all" value={cliente.formaPagamento} onChange={e => setCliente({...cliente, formaPagamento: e.target.value})}>
                    <option value="">Selecione...</option>
                    {FORMAS_PAGAMENTO.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Endereço Completo</label>
                <input required className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:border-orange-600 outline-none transition-all" value={cliente.endereco} onChange={e => setCliente({...cliente, endereco: e.target.value})} placeholder="Rua, Número, Complemento" />
              </div>
            </div>
            
            <button disabled={isSubmitting} type="submit" className="w-full bg-orange-600 text-white font-black py-6 rounded-3xl shadow-xl hover:bg-orange-700 transition-all active:scale-[0.98] disabled:opacity-50">
              {isSubmitting ? 'Processando...' : 'Confirmar e Enviar para o WhatsApp'}
            </button>
          </form>
        </div>
      )}

      <CustomerAuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
        onSuccess={loadCustomerData} 
      />

      <Cart items={cart} isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} onUpdateQuantity={updateQuantity} onCheckout={() => { setIsCartOpen(false); setIsCheckoutOpen(true); }} />
      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; } .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; } .custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }`}</style>
    </div>
  );
};
